import _ from 'lodash';
import {createSelector} from 'reselect';

import {isTeamChannel} from '../../../helpers/models/channelHelpers';
import {isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {DraftMessageModel} from '../../../models/api/messageModel';
import {AppState} from '../../appState';
import {getChannels} from '../../data/channels/channelsSelectors';
import {getConversations, getMessages} from '../../data/dataSelectors';
import {getInboxes} from '../../data/inboxes/inboxesSelectors';
import {getUser} from '../../data/teammates/teammatesSelectors';
import {CellsContext, makeDefaultCellsContext} from '../cells/context/cellsContext';
import {getFlatPanels} from '../panelsSelectors';
import {ComposerPanelId, PanelTypesEnum} from '../panelStateBase';

/*
 * Composer panel.
 */

interface ComposerPanelProps {
  panelId: ComposerPanelId;
}

function getPanel(state: AppState, props: ComposerPanelProps) {
  return state.panels[props.panelId];
}

export function makeGetComposerPanel() {
  return createSelector(getPanel, (panel) => {
    // A corresponding panel should exist, otherwise we have a problem.
    if (!panel || panel.type !== PanelTypesEnum.COMPOSER) {
      throw new Error('This composer panel could not be found.');
    }

    return panel;
  });
}

/*
 * Selectors.
 */

export const getComposerPanelsRooms = wrapInShallowSelector(
  createSelector([getFlatPanels, getMessages], (panels, messages) =>
    _(panels)
      .map((panel) => panel.type === PanelTypesEnum.COMPOSER && panel)
      .compact()
      .map((panel) => messages[panel.messageUid])
      .compact()
      .map((message) => message.conversationId)
      .compact()
      .uniq()
      .map((conversationId) => `conversation-${conversationId}`)
      .value(),
  ),
);

export const getComposerPanelsConversationIds = createSelector(
  [getFlatPanels, getMessages],
  (panels, messages) =>
    _(panels)
      .map((panel) => panel.type === PanelTypesEnum.COMPOSER && panel)
      .compact()
      .map((panel) => messages[panel.messageUid])
      .compact()
      .map((message) => message.conversationId)
      .compact()
      .uniq()
      .value(),
);

export const getFirstComposerPanel = createSelector([getFlatPanels], (panels) =>
  _(panels)
    .map((panel) => panel.type === PanelTypesEnum.COMPOSER && panel)
    .compact()
    .first(),
);

export const getFirstComposerPanelId = createSelector([getFirstComposerPanel], (panel) => panel?.id);

/*
 * Instance selectors.
 */

type DraftMessageSelector<P> = AppParametricSelector<P, DraftMessageModel>;
type CellsContextSelector<P> = AppParametricSelector<P, CellsContext | undefined>;
type ChannelSelector<P> = AppParametricSelector<P, ChannelModel>;
type OptionalConversationSelector<P> = AppParametricSelector<P, ConversationModel | undefined>;

export function makeGetComposerChannel<P>(getMessage: DraftMessageSelector<P>) {
  return createSelector([getMessage, getChannels], (message, channels) => {
    const channel = channels[message.fromChannelId];
    if (!channel) {
      throw new Error('The channel for the current draft could not be found.');
    }

    return channel;
  });
}

export function makeGetComposerConversation<P>(getMessage: DraftMessageSelector<P>) {
  return createSelector(
    [getMessage, getConversations],
    (message, conversations) => conversations[message.conversationId],
  );
}

export function makeGetComposerNamespace<P>(
  getChannel: ChannelSelector<P>,
  getConversation: OptionalConversationSelector<P>,
  getMessage: DraftMessageSelector<P>,
) {
  return createSelector([getChannel, getMessage, getConversation], (channel, message, conversation) => {
    const isReply = Boolean(message.inReplyToId);
    if (conversation && isReply && isTeamNamespace(conversation.namespace)) {
      return conversation.namespace;
    }

    return channel.namespace;
  });
}

export function makeGetComposerCellsContext<P>(
  getChannel: ChannelSelector<P>,
  getCellsContext: CellsContextSelector<P>,
) {
  return wrapInShallowSelector(
    createSelector(
      [getUser, getInboxes, getChannel, getCellsContext],
      (user, inboxes, channel, cellsContext) => {
        // If a cells context was provided, use it.
        if (cellsContext) {
          return cellsContext;
        }

        // Otherwise, build a new one from the channel.
        const isInTeamContext = isTeamChannel(channel);
        return makeDefaultCellsContext(user, isInTeamContext);
      },
    ),
  );
}
